import { useState, useEffect, useRef } from 'react'
import { Form, Head, router } from '@inertiajs/react'
import InputError from '@/components/input-error'
import TextLink from '@/components/text-link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AuthLayout from '@/layouts/auth-layout'
import { verifyOtpPath, requestOtpPath, signInPath } from '@/routes'

type VerifyOtpProps = {
  email: string
  message?: string
  error?: string
}

const OTP_EXPIRY_SECONDS = 600 // 10 minutes

export default function VerifyOtp({ email, message, error }: VerifyOtpProps) {
  const [timeLeft, setTimeLeft] = useState(OTP_EXPIRY_SECONDS)
  const [isResending, setIsResending] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function handleResend() {
    setIsResending(true)
    router.post(requestOtpPath(), { email }, {
      onFinish: () => {
        setIsResending(false)
        setTimeLeft(OTP_EXPIRY_SECONDS)
      }
    })
  }

  const isExpired = timeLeft === 0

  return (
    <AuthLayout
      title="Enter your login code"
      description={`We sent a 6-digit code to ${email}`}
    >
      <Head title="Verify Login Code" />

      <div className="p-6">
        {message && (
        <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
          {message}
        </div>
      )}

      <Form
        method="post"
        action={verifyOtpPath()}
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <div className="grid gap-6">
            <input type="hidden" name="email" value={email} />

            <div className="grid gap-2">
              <Label htmlFor="otp_code">Login Code</Label>
              <Input
                ref={inputRef}
                id="otp_code"
                name="otp_code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                autoComplete="one-time-code"
                placeholder="000000"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                disabled={isExpired}
              />
              <InputError messages={errors.otp_code} />
              {error && <InputError messages={[error]} />}
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className={`${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
                {isExpired ? 'Code expired' : `Expires in ${formatTime(timeLeft)}`}
              </span>
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? 'Sending...' : 'Resend code'}
              </button>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={processing || isExpired}
            >
              {processing && <Spinner />}
              Verify & Log In
            </Button>
          </div>
        )}
      </Form>

      <div className="text-muted-foreground mt-6 text-center text-sm">
        <TextLink href={signInPath()}>
          ← Back to login
        </TextLink>
      </div>
      </div>
    </AuthLayout>
  )
}
